/**
 * Centralized logging utility
 * In production, logs are suppressed or sent to external service
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  private log(level: LogLevel, message: string, ...args: any[]): void {
    if (!this.isDevelopment && level !== 'error') {
      // In production, only log errors
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'error':
        console.error(prefix, message, ...args);
        // In production, send to error tracking service (Sentry, etc.)
        break;
      case 'warn':
        console.warn(prefix, message, ...args);
        break;
      case 'info':
        console.info(prefix, message, ...args);
        break;
      case 'debug':
        if (this.isDevelopment) {
          console.debug(prefix, message, ...args);
        }
        break;
      default:
        console.log(prefix, message, ...args);
    }
  }

  public info(message: string, ...args: any[]): void {
    this.log('info', message, ...args);
  }

  public warn(message: string, ...args: any[]): void {
    this.log('warn', message, ...args);
  }

  public error(message: string, error?: Error | unknown, ...args: any[]): void {
    if (error instanceof Error) {
      this.log('error', message, error.message, error.stack, ...args);
    } else {
      this.log('error', message, error, ...args);
    }
  }

  public debug(message: string, ...args: any[]): void {
    this.log('debug', message, ...args);
  }

  // Convenience methods for common use cases
  public apiCall(method: string, url: string, data?: any): void {
    this.debug(`API ${method}`, url, data);
  }

  public apiResponse(url: string, status: number, data?: any): void {
    this.debug(`API Response`, url, `Status: ${status}`, data);
  }

  public apiError(url: string, error: Error | unknown): void {
    this.error(`API Error: ${url}`, error);
  }

  public component(componentName: string, action: string, data?: any): void {
    this.debug(`[${componentName}] ${action}`, data);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export class for testing
export default Logger;
